'use client';

import { useState, useMemo } from 'react';
import FAQCategory from './FAQCategory';
import SearchBar from './SearchBar';
import CategoryFilter from './CategoryFilter';
import QuickLinks from './QuickLinks';
import CTASection from './CTASection';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

interface QuickLink {
  title: string;
  description: string;
  href: string;
  icon: string;
}

interface FAQInteractiveProps {
  faqData: FAQItem[];
  quickLinks: QuickLink[];
}

export default function FAQInteractive({ faqData, quickLinks }: FAQInteractiveProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const categories = useMemo(() => {
    const cats = ['All', ...Array.from(new Set(faqData.map((item) => item.category)))];
    return cats;
  }, [faqData]);

  const filteredFAQs = useMemo(() => {
    let filtered = faqData;

    if (activeCategory !== 'All') {
      filtered = filtered.filter((item) => item.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.question.toLowerCase().includes(query) ||
          item.answer.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [faqData, activeCategory, searchQuery]);

  const groupedFAQs = useMemo(() => {
    const groups: { [key: string]: FAQItem[] } = {};
    filteredFAQs.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, [filteredFAQs]);

  const handleToggle = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="font-headline text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-4">
            Frequently Asked Questions
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Expert answers to your desert patio cleaning questions. Learn about our processes, pricing, and Phoenix-specific solutions.
          </p>
        </div>

        {/* Search Bar */}
        <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />

        {/* Quick Links */}
        <QuickLinks links={quickLinks} />

        {/* Category Filter */}
        <CategoryFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />

        {/* FAQ Categories */}
        <div className="mb-16">
          {Object.keys(groupedFAQs).length > 0 ? (
            Object.entries(groupedFAQs).map(([category, items]) => (
              <FAQCategory
                key={category}
                title={category}
                items={items}
                expandedId={expandedId}
                onToggle={handleToggle}
              />
            ))
          ) : (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-muted-foreground"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="font-headline text-xl font-semibold text-foreground mb-2">
                No Results Found
              </h3>
              <p className="font-body text-muted-foreground mb-6">
                We couldn&apos;t find any questions matching your search. Try different keywords or browse all categories.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('All');
                }}
                className="px-6 py-3 bg-primary text-primary-foreground font-body font-semibold rounded-lg hover:bg-primary/90 transition-brand"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <CTASection />
      </div>
    </div>
  );
}